import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import { AbstractListingCalculator } from "@component/assets/AbstractListingCalculator/AbstractListingCalculator";
import type { ResultListResponse } from "@component/assets/AbstractListingCalculator/types";
import { LandingHeader } from "@landing/components/LandingHeader";
import { LandingHeaderLayout } from "@landing/components/LandingHeaderLayout";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import autobind from "autobind-decorator";
import { Stack, TimelineUi3, TimelineUi3Item, Typography, getUrlParameters } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { CorporateBankAccountResultList } from "./CorporateBankAccountResultList";
import { CorporateBankAccountSearchForm } from "./CorporateBankAccountSearchForm";
import { calculate, exportXls, responseToResult, searchParamsToRequest } from "./domain";
import type {
	CorporateBankAccountRequest,
	CorporateBankAccountResponseItem,
	CorporateBankAccountSearchParams,
	CorporateBankAccountSort,
} from "./types";
import "./style.scss";

export class CorporateBankAccountCalculator extends AbstractListingCalculator<
	CorporateBankAccountSearchParams,
	CorporateBankAccountRequest,
	CorporateBankAccountResponseItem,
	CorporateBankAccountSort
> {
	// EventParams
	protected readonly productTypes: string = "SME_ACCOUNT";
	protected readonly componentType: EventComponentType = "listing-calculator";

	protected getDefaultValues(): CorporateBankAccountSearchParams {
		let fields = {
			outgoingTransfer: undefined,
		};

		fields = {
			...fields,
			...(getUrlParameters(window.location.href) as CorporateBankAccountSearchParams),
		};
		// boolean type fix:
		["hasLoan", "needLoad", "needPOSTerminal", "refinance"].forEach((fieldName) => {
			const value = fields[fieldName];

			if (value !== undefined) {
				fields[fieldName] = value === "true";
			}
		});

		return fields;
	}

	protected renderContent(): ReactNode {
		const { onExport: _onExport, ...props } = this.getSearchFormProps();

		return (
			<>
				<LandingHeader id="kalkulator" theme="corporate">
					<LandingHeaderLayout template="small-calculator">
						<Stack
							gap={{
								xs: 4,
								lg: 5,
							}}
							alignItems={"start"}
						>
							<div>
								<h1>
									<Typography variant="title-1" gutterBottom>
										Vállalkozói bankszámla kereső
									</Typography>
								</h1>
								<Typography variant="subtitle-1">
									Hogyan juthatsz el az optimális bankszámlához?
								</Typography>
							</div>
							<TimelineUi3>
								<TimelineUi3Item>
									Válaszd ki a <strong>céged működésére legfontosabb</strong> jellemzőket
								</TimelineUi3Item>
								<TimelineUi3Item>
									Tekintsd át ezek alapján, mik a <strong>cégprofilodra ajánlott</strong> bankszámlák
								</TimelineUi3Item>
								<TimelineUi3Item>
									Adj le érdeklődést, hogy a Bankmonitor szakértője{" "}
									<strong>személyre szabott, teljes költségszámítást tartalmazó összesítést</strong>{" "}
									készíthessen vállalkozásod számára
								</TimelineUi3Item>
								<TimelineUi3Item>
									Így a legkényelmesebb módon, sorban állás nélkül juthatsz bankszámlához
								</TimelineUi3Item>
							</TimelineUi3>
						</Stack>
						<CorporateBankAccountSearchForm className="mx-auto" {...props} />
					</LandingHeaderLayout>
				</LandingHeader>
				<CorporateBankAccountResultList {...this.getResultListProps()} />
			</>
		);
	}

	protected getRequestFromSearchParams(searchParams: CorporateBankAccountSearchParams): CorporateBankAccountRequest {
		return searchParamsToRequest(searchParams, this.state?.sort);
	}

	protected async getResult(
		request: CorporateBankAccountRequest,
		searchParams: CorporateBankAccountSearchParams,
	): Promise<any> {
		return calculate(request, searchParams);
	}

	protected getResultList(
		request: CorporateBankAccountRequest,
		searchParams: CorporateBankAccountSearchParams,
		response: any,
	): ResultListResponse<CorporateBankAccountResponseItem> {
		return responseToResult(request, searchParams, response);
	}

	@autobind
	protected async onExport(searchParams: CorporateBankAccountSearchParams): Promise<string> {
		return exportXls(searchParams);
	}

	protected getClassName(): string {
		return `${super.getClassName()} corporate-bank-account`;
	}

	protected getProductType(): ProductType {
		return "SME_ACCOUNT";
	}

	protected getBasicSearchFields(): string[] {
		return [];
	}

	protected getComponentName(): string {
		return "CorporateBankAccountCalculator";
	}
}
